import React from "react";
import { Button, CircularProgress, MuiThemeProvider } from "@material-ui/core";
import ArrowBack from "@material-ui/icons/ArrowBack";
import ArrowForward from "@material-ui/icons/ArrowForward";
import Check from "@material-ui/icons/Check";
import Phone from "@material-ui/icons/Phone";
import LanguageSwitch from "../../../i18n/LanguageSwitch";
import { SecureNote, useTranslation } from "./ui";
import { PAYMENT_STEP } from "./values";
import theme from "./theme";
import logo from "./logo.png";
import "./styles.css";

const STEP_LABELS = [
  "Get Started",
  "Business",
  "Owner",
  "Sales",
  "Banking",
  "Payment",
  "Review"
];

// Only steps before the current one can be opened from here. Moving forward
// always goes through Next, which validates, saves and prepares the next step.
export function Progress({ activeStep, application, onStepClick, disabled, labels }) {
  const t = useTranslation();
  const steps = labels.map((label, step) => ({ label, step })).filter(
    ({ step }) => !(step === PAYMENT_STEP && application.withoutPayment)
  );
  const position = Math.max(
    0,
    steps.findIndex(({ step }) => step === activeStep)
  );
  const reached = Math.max(activeStep, application.activeStep || 0);
  return (
    <nav
      className="merchant-progress"
      aria-label={t("Application progress")}
      style={{ "--merchant-steps": steps.length }}
    >
      <p className="merchant-progress-count">
        {t("Step {current} of {total}", {
          current: position + 1,
          total: steps.length
        })}
      </p>
      <div className="merchant-progress-track" aria-hidden="true">
        <span style={{ width: `${(position / (steps.length - 1)) * 100}%` }} />
      </div>
      <ol>
        {steps.map(({ label, step }, index) => {
          const state =
            step === activeStep ? "current" : step <= reached ? "done" : "todo";
          const content = (
            <>
              <span className="merchant-progress-dot" aria-hidden="true">
                {state === "done" ? <Check /> : index + 1}
              </span>
              <span className="merchant-progress-label">{t(label)}</span>
            </>
          );
          return (
            <li key={step} className={`merchant-progress-${state}`}>
              {step < activeStep && onStepClick ? (
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => onStepClick(step)}
                >
                  {content}
                </button>
              ) : (
                <span aria-current={state === "current" ? "step" : undefined}>
                  {content}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

// A step inside a CRM form that keeps the form's own stepper and buttons.
// `legacy` gives the old CRM-only steps the new heading and field look.
export function EmbeddedStep({ children, activeStep, wide, legacy, progress }) {
  return (
    <MuiThemeProvider theme={theme}>
      <div
        className={`merchant-ui merchant-embedded merchant-step-${activeStep}${
          legacy ? " merchant-legacy" : ""
        }`}
      >
        <main className={`merchant-content${wide ? " merchant-wide" : ""}`}>
          {progress}
          {children}
        </main>
      </div>
    </MuiThemeProvider>
  );
}

export default function Shell({
  children,
  application,
  locale,
  onLocaleChange,
  activeStep,
  onBack,
  onNext,
  onStepClick,
  loading,
  nextDisabled,
  wide,
  confirmation,
  embedded,
  actions,
  stepLabels = STEP_LABELS
}) {
  const t = useTranslation();
  return (
    <MuiThemeProvider theme={theme}>
      <div
        className={`merchant-ui merchant-step-${activeStep}${
          embedded ? " merchant-embedded" : ""
        }`}
      >
        {!embedded && (
          <header className="merchant-masthead">
            <div className="merchant-masthead-inner">
              <img src={logo} width="103" height="55" alt="POS Brokers" />
              <div className="merchant-help">
                <Phone aria-hidden="true" />
                <div>
                  <strong>
                    {t("Need Help? Call")}{" "}
                    <a href="tel:8882418393">888-241-8393</a>
                  </strong>
                  <span>{t("Speak with an application specialist.")}</span>
                </div>
              </div>
              <LanguageSwitch locale={locale} onChange={onLocaleChange} />
            </div>
          </header>
        )}
        <main
          className={`merchant-content${wide ? " merchant-wide" : ""}`}
          aria-busy={loading || undefined}
        >
          {!confirmation && (
            <Progress
              activeStep={activeStep}
              application={application}
              onStepClick={onStepClick}
              disabled={loading}
              labels={stepLabels}
            />
          )}
          {children}
          {!confirmation && actions}
          {!confirmation && !actions && (
            <>
              <nav
                className="merchant-actions"
                aria-label={t("Application navigation")}
              >
                <Button
                  variant="contained"
                  disabled={activeStep === 0 || loading}
                  onClick={onBack}
                  startIcon={<ArrowBack />}
                >
                  {t("Back")}
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  disabled={loading || nextDisabled}
                  onClick={onNext}
                  endIcon={!loading && <ArrowForward />}
                >
                  {loading ? (
                    <CircularProgress
                      color="inherit"
                      size={24}
                      aria-label={t("Saving")}
                    />
                  ) : (
                    t(activeStep === 6 ? "Submit Application" : "Next")
                  )}
                </Button>
              </nav>
              <div className="merchant-security">
                <SecureNote />
              </div>
            </>
          )}
        </main>
        {!embedded && (
          <footer className="merchant-footer">
            <div>
              {t("Need Help? Call")} <a href="tel:8882418393">888-241-8393</a>
            </div>
            {activeStep === 0 && <div>{t("Speak with an application specialist.")}</div>}
            {application.id && (
              <div>
                {t("Application Number:")} {application.id}
              </div>
            )}
          </footer>
        )}
      </div>
    </MuiThemeProvider>
  );
}
